/**
 * Legacy Routes Bridge
 *
 * Re-exports legacy routes from src/router.js wrapped with LegacyAppShell layout.
 * This allows the v2 router to render legacy pages through the original App.vue shell.
 */
import type { RouteRecordRaw } from 'vue-router';

// Lazy import to avoid circular dependency:
// LegacyAppShell → src/App.vue → @v2/layout → MainLayout.vue
// Synchronous import here would cause TDZ errors during route navigation.
const LegacyAppShell = () => import('@v2/legacy-bridge/components/LegacyAppShell.vue');

// Import legacy routes and router utilities
import legacyRouter from '@/router';
// Import legacy store for use in v2 route meta.canView functions
import store from '@/store';

// Get the raw routes from the legacy router
const rawLegacyRoutes = legacyRouter.options.routes;

/**
 * Routes that have been migrated to v2 and should be excluded from legacy routing.
 * Add route paths here when migrating a page to v2.
 */
const migratedRoutes = [
  '/profile',
  '/profile/edit',
  '/inbox',
  '/discussions',
  '/discussions/create',
  '/discussions/:uuid',
  '/contacts',
  '/debate/:documentID',
  '/curatordebate/:documentID',
  '/publicview/debate/:documentID',
];

/**
 * Filter out routes that have been migrated to v2
 */
const filteredLegacyRoutes = (rawLegacyRoutes as RouteRecordRaw[]).filter(
  route => !migratedRoutes.includes(route.path as string)
);

/**
 * Wrap all legacy routes under LegacyAppShell layout.
 * Uses a parent route with children to avoid duplicating the wrapper for each route.
 */
export const legacyRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    component: LegacyAppShell,
    children: filteredLegacyRoutes,
  },
];

// Re-export the routesMap utility for legacy code that uses it
export const legacyRoutesMap = legacyRouter.routesMap;
export const legacyPushRoute = legacyRouter.pushRoute;

// Re-export the legacy store for v2 routes that need meta.canView
export const legacyStore = store;
